import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trash2, RotateCcw, Search, X, AlertTriangle } from "lucide-react";

import { DeleteOrRestoreStakingRiskProductThunk, GetAllDeletedStakingRiskProductsThunk } from "../../Store/Thunks/StakingRiskThunk";
import TrashProductCard from "../../Components/StakingRisk/dashboard/TrashProductCard";
import { Button } from "../../Components/StakingRisk/ui/button";
import {Input} from "../../Components/StakingRisk/ui/input";
import { DELETE_TYPES } from "../../StakingRisk/constants";

export default function TrashBox() {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const deletedProducts = useSelector((state) => state.StakingRiskReducer.deletedstakingRiskList);
  const deletedStakingRiskPermanently = useSelector(
    (state) => state.StakingRiskReducer.deleted
  );
   const deletedorrestore = useSelector(
    (state) => state.StakingRiskReducer.restoreordelete
  );
 
  useEffect(() => {
    const fetchDeletedProducts = async () => {    
      dispatch(GetAllDeletedStakingRiskProductsThunk(searchQuery?.trim()));   
    };
  
    fetchDeletedProducts();
  }, [deletedStakingRiskPermanently, deletedorrestore]);

    const handleRestoreAll = async () => {
        if (window.confirm("Are you sure you want to restore all products?")) {
          await dispatch(DeleteOrRestoreStakingRiskProductThunk(DELETE_TYPES.RESTORE))
          await dispatch(GetAllDeletedStakingRiskProductsThunk());
        }    
      };

      const handleEmptyTrash = async () => {
        if (window.confirm("Are you sure you want to permanently delete all products in trash? This cannot be undone!")) {
          await dispatch(DeleteOrRestoreStakingRiskProductThunk(DELETE_TYPES.DELETE))
          await dispatch(GetAllDeletedStakingRiskProductsThunk());
        }
      };

      const clearSearch = () => {
        setSearchQuery("");
      };

      const filteredProducts = deletedProducts?.filter(product => 
        searchQuery.trim() === "" || product.name?.toLowerCase().includes(searchQuery.toLowerCase().trim())
      );
  return (
    <div className="p-6 max-w-7xl mx-auto">        
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Trash2 className="w-8 h-8 text-red-600" />
            Trash Box
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Restore or permanently delete products
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">        
          <Link to={"/stakingrisk/dashboard"}>
            <Button variant="outline">
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
      {deletedProducts?.length > 0 && (
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search deleted products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-gray-400 hover:text-gray-600"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRestoreAll}
              variant="outline"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              disabled={filteredProducts.length === 0}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Restore All
            </Button>
            <Button
              onClick={handleEmptyTrash}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              disabled={filteredProducts.length === 0}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Empty Trash
            </Button>
          </div>
        </div>
      )}

      {searchQuery && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {filteredProducts.length === 0 ? (
            <span>No deleted products found for "{searchQuery}"</span>
          ) : (
            <span>
              Showing {filteredProducts.length} deleted product{filteredProducts.length !== 1 ? 's' : ''} 
              matching "{searchQuery}"
            </span>
          )}
        </div>
      )}

      {deletedProducts?.length === 0 ? (
        <div className="text-center p-10 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <Trash2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">Trash is empty</h3>
          <p className="text-gray-500 dark:text-gray-400">
            No deleted products found. Products moved to trash will appear here.
          </p>
        </div>
      ) : filteredProducts?.length === 0 ? (
        <div className="text-center p-10 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <AlertTriangle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">No matching products</h3>
          <p className="text-gray-500 dark:text-gray-400">
            No deleted products match your search criteria.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProducts?.map(product => (
            <TrashProductCard 
              key={product.id} 
              product={product}                        
            />
          ))}
        </div>
      )}
    </div>
  );
}